import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronLeft, ChevronRight, Home, Phone, TrendingUp, MessageCircle } from 'lucide-react';
import { optimizedImageUrl } from '@/lib/imageOptimizer';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPriceRange } from '@/lib/formatPrice';

interface Props { projects: any[]; }

export function TrendingProjectsTwoRow({ projects }: Props) {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const { displayCurrency, rate } = useCurrency();

  // single row, mobile shows exactly 2 cards

  const scroll = (dir: 'left' | 'right') => {
    if (!ref.current) return;
    const w = ref.current.clientWidth * 0.6;
    ref.current.scrollBy({ left: dir === 'left' ? -w : w, behavior: 'smooth' });
  };

  // auto-advance
  useEffect(() => {
    if (!projects.length) return;
    const t = setInterval(() => {
      const el = ref.current; if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      if (el.scrollLeft >= max - 4) el.scrollTo({ left: 0, behavior: 'smooth' });
      else el.scrollBy({ left: el.clientWidth * 0.6, behavior: 'smooth' });
    }, 5000);
    return () => clearInterval(t);
  }, [projects.length]);

  if (!projects.length) return null;

  const Card = ({ p }: { p: any }) => {
    const name = typeof p.name === 'object' ? p.name?.en || '' : p.name || '';
    const img = p.project_images?.find((i: any) => i.is_primary)?.url || p.project_images?.[0]?.url;
    const src = optimizedImageUrl(img || 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400', { width: 400, height: 300, quality: 75 });
    const priceRange = p.price_min ? formatPriceRange(p.price_min, p.price_max, p.currency || 'ETB', displayCurrency, rate) : '';
    const dev = p.developer_profiles;
    const devName = dev?.company_name || '';
    const phone = dev?.phone;
    const wa = dev?.whatsapp || dev?.phone;

    const stop = (e: React.MouseEvent) => { e.stopPropagation(); };

    return (
      <div onClick={() => navigate(`/projects/${p.slug || p.id}`)}
        className="snap-start flex-shrink-0 w-[calc(50%-6px)] sm:w-[260px] rounded-xl border bg-card overflow-hidden hover:shadow-lg cursor-pointer group">
        <div className="relative aspect-[4/3]">
          <img src={src} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        </div>
        <div className="p-2.5 space-y-1">
          {priceRange && <div className="font-bold text-primary text-sm">{priceRange}</div>}
          <div className="font-semibold text-xs leading-tight truncate">{name}</div>
          <div className="text-[11px] text-muted-foreground truncate">{p.city}{p.sub_city ? `, ${p.sub_city}` : ''}</div>
          <div className="flex items-center gap-2 pt-1 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-0.5"><Home className="h-3 w-3" /> {p.status?.replace('_',' ') || 'Apartments'}</span>
            {p.total_units && <span className="flex items-center gap-0.5"><Building2 className="h-3 w-3" /> {p.available_units || p.total_units}</span>}
          </div>
          {devName && (
            <div className="flex items-center justify-between pt-2 mt-1 border-t gap-1">
              <span className="text-[10px] font-medium text-muted-foreground truncate flex-1">{devName}</span>
              <div className="flex items-center gap-1 shrink-0">
                {phone && (
                  <a href={`tel:${phone}`} onClick={stop} aria-label="Call" className="h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                    <Phone className="h-3.5 w-3.5" />
                  </a>
                )}
                {wa && (
                  <a href={`https://wa.me/${wa.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener" onClick={stop} aria-label="WhatsApp" className="h-7 w-7 rounded-full bg-whatsapp text-white flex items-center justify-center">
                    <MessageCircle className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="container py-6 md:py-10">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          <h2 className="font-display text-lg md:text-2xl font-bold">Trending Projects</h2>
        </div>
      </div>
      <div className="relative">
        <button onClick={() => scroll('left')} aria-label="Scroll trending left" className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-background/90 border shadow flex items-center justify-center">
          <ChevronLeft className="h-4 w-4 text-primary" />
        </button>
        <button onClick={() => scroll('right')} aria-label="Scroll trending right" className="absolute -right-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-background/90 border shadow flex items-center justify-center">
          <ChevronRight className="h-4 w-4 text-primary" />
        </button>
        <div ref={ref} className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory pb-2">
          {projects.map(p => <Card key={p.id} p={p} />)}
        </div>
      </div>
    </section>
  );
}
